import React, { useCallback, useLayoutEffect, useRef, useState } from "react"
import { Box } from "@mui/material"
import Button from "../../../generic-shared-components/components/Button"
import FullscreenModal from "../../../generic-shared-components/components/FullscreenModal"
import { LegalDocument } from "../../../../../../../../packages/mp-shared-components/src/legal/LegalDocument"
import { DocumentType, legalDocuments } from "../../../../../../../../packages/mp-shared-components/src/legal/documents"
import { useStyles } from "./styles"

interface Props {
  documentType: DocumentType
  onClose: () => void
}

interface DocPage {
  type: DocumentType
  savedFocus?: HTMLElement
  savedScrollTop?: number
}

const DocumentModal: React.FC<Props> = ({ documentType, onClose }) => {
  const { classes } = useStyles()
  const [docStack, setDocStack] = useState<DocPage[]>([{ type: documentType }])
  const panelRefs = useRef<Array<HTMLDivElement | null>>([])
  const shouldRestorePosition = useRef(false)
  const activePageIndex = docStack.length - 1
  const activePage = docStack[activePageIndex]

  const openDocPage = useCallback((type: DocumentType) => {
    setDocStack(previousStack => {
      const lastIndex = previousStack.length - 1
      if (previousStack[lastIndex].type === type) return previousStack
      return [
        ...previousStack.slice(0, -1),
        {
          ...previousStack[lastIndex],
          savedFocus: document.activeElement as HTMLElement,
          savedScrollTop: panelRefs.current[lastIndex]?.scrollTop
        },
        { type }
      ]
    })
  }, [])

  const returnToPreviousPage = () => {
    shouldRestorePosition.current = true
    setDocStack(previousStack => previousStack.slice(0, -1))
  }

  const focusPageTop = useCallback(() => {
    const activePanel = panelRefs.current[activePageIndex]
    const firstReadableElement = activePanel?.querySelector<HTMLElement>("p, h2, h3")
    if (firstReadableElement) {
      firstReadableElement.tabIndex = -1
      firstReadableElement.focus({ preventScroll: true })
    } else activePanel?.focus()
  }, [activePageIndex])

  useLayoutEffect(() => {
    const activePanel = panelRefs.current[activePageIndex]
    if (shouldRestorePosition.current) {
      activePage.savedFocus?.focus({ preventScroll: true })
      if (activePanel) activePanel.scrollTop = activePage.savedScrollTop || 0
      shouldRestorePosition.current = false
    } else {
      focusPageTop()
    }
  }, [activePageIndex, activePage.savedFocus, activePage.savedScrollTop, focusPageTop])

  return (
    <FullscreenModal
      open
      whiteBackground
      title={legalDocuments[activePage.type].title}
      onClose={onClose}
      onEntered={focusPageTop}
      dataAutomationId="legal-document-modal"
      outerClasses={{ content: classes.modalContent }}
      actions={
        <Box display="flex" justifyContent="space-between" width="100%">
          {docStack.length > 1 ? (
            <Button size="md" onClick={returnToPreviousPage} data-automation-id="legal-document-back-button">
              Back
            </Button>
          ) : <Box />}
          <Button size="md" onClick={onClose} data-automation-id={`close-${activePage.type}`}>
            Close
          </Button>
        </Box>
      }
    >
      {docStack.map((docPage, pageIndex) => (
        <Box
          key={`${docPage.type}-${pageIndex}`}
          ref={(element: HTMLDivElement | null) => { panelRefs.current[pageIndex] = element }}
          hidden={pageIndex !== activePageIndex}
          tabIndex={-1}
          className={classes.page}
          data-automation-id={`legal-document-${docPage.type}-page`}
        >
          <LegalDocument content={legalDocuments[docPage.type]} onOpenDocument={openDocPage} />
        </Box>
      ))}
    </FullscreenModal>
  )
}

export default DocumentModal
